<template>
  <aside class="sidebar">
    <div class="sidebar-logo">
      <Logo />
    </div>
    
    <nav class="sidebar-nav">
      <router-link v-if="!isAdmin" to="/home" class="nav-item" active-class="active">
        <v-icon>mdi-home</v-icon>
        <span>Home</span>
      </router-link>
      
      <!-- Show My Payouts only after profile loaded and user not working with Coke -->
      <router-link v-if="!isAdmin && expertProfileLoaded && !worksWithCoke" to="/my-payouts" class="nav-item" active-class="active">
        <v-icon>mdi-currency-usd</v-icon>
        <span>My Payouts</span>
      </router-link>
      
      <router-link v-if="isAdmin" to="/assessments" class="nav-item" active-class="active">
        <v-icon>mdi-clipboard-text-outline</v-icon>
        <span>Assessments</span>
      </router-link>
      
      <router-link v-if="isAdmin" to="/vis" class="nav-item" active-class="active">
        <v-icon>mdi-image-multiple-outline</v-icon>
        <span>VIS Library</span>
      </router-link>
      
      <router-link v-if="isAdmin" to="/experts" class="nav-item" active-class="active">
        <v-icon>mdi-account-group-outline</v-icon>
        <span>Experts</span>
      </router-link>
      
      <router-link v-if="isAdmin" to="/admin/payments" class="nav-item" active-class="active">
        <v-icon>mdi-currency-usd</v-icon>
        <span>Payments</span>
      </router-link>
      
      <router-link to="/account" class="nav-item" active-class="active">
        <v-icon>mdi-account-cog-outline</v-icon>
        <span>Account</span>
      </router-link>
      
      <router-link to="/help" class="nav-item" active-class="active">
        <v-icon>mdi-help-circle-outline</v-icon>
        <span>Help Center</span>
      </router-link>
    </nav>
    
    <div class="sidebar-footer">
      <div class="user-info" @click="toggleUserMenu">
        <div class="user-avatar">
          <img v-if="expertProfile && expertProfile.photoUrl" :src="expertProfile.photoUrl" alt="User avatar">
          <div v-else class="avatar-placeholder">{{ userInitials }}</div>
        </div>
        <div class="user-details">
          <div class="user-name">
            {{ getUserDisplayName }}
          </div>
          <v-icon size="18">mdi-chevron-down</v-icon>
        </div>
      </div>
      <div v-if="showUserMenu" class="user-menu">
        <div class="menu-item" @click="logout">
          <v-icon size="18">mdi-logout</v-icon>
          <span>Logout</span>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import { mapGetters } from 'vuex'
import Logo from './Logo.vue'
import apiService from '@/services/apiService'

export default {
  name: 'AppSidebar',
  components: {
    Logo
  },
  data() {
    return {
      showUserMenu: false,
      expertProfile: null,
      expertProfileLoaded: false
    }
  },
  computed: {
    ...mapGetters(['isAdmin', 'isSuperAdmin']),
    user() {
      return this.$store.state.user || { username: 'Jane Doe' }
    },
    // Determine if the current user should be restricted from payout functionality
    worksWithCoke() {
      // Priority to ExpertProfile field if already fetched
      if (this.expertProfile && (this.expertProfile.worksForCoke !== undefined)) {
        return !!this.expertProfile.worksForCoke;
      }
      // Fallback to potential snake_case field from either expertProfile or user object
      if (this.expertProfile && (this.expertProfile.works_with_coke !== undefined)) {
        return !!this.expertProfile.works_with_coke;
      }
      if (this.user && (this.user.works_with_coke !== undefined)) {
        return !!this.user.works_with_coke;
      }
      return false;
    },
    userInitials() {
      if (this.expertProfile && (this.expertProfile.first_name || this.expertProfile.last_name)) {
        // Prefer initials from first and last name fields directly
        const firstInitial = this.expertProfile.first_name
          ? this.expertProfile.first_name.charAt(0).toUpperCase()
          : '';
        const lastInitial = this.expertProfile.last_name
          ? this.expertProfile.last_name.charAt(0).toUpperCase()
          : '';
        return (firstInitial + lastInitial) || 'G';
      }
      if (!this.user) return 'G';
      
      if (this.user.first_name && this.user.last_name) {
        // Get initials from first and last name (e.g., "John Doe" -> "JD")
        return this.user.first_name.charAt(0).toUpperCase() + 
               this.user.last_name.charAt(0).toUpperCase();
      } else if (this.user.first_name) {
        return this.user.first_name.charAt(0).toUpperCase();
      } else if (this.user.last_name) {
        return this.user.last_name.charAt(0).toUpperCase();
      } else if (this.user.username) {
        return this.user.username.charAt(0).toUpperCase();
      }
      return 'G'; // Guest
    },
    getUserDisplayName() {
      // Determine the display name, preferring the expert profile first & last name
      let name = 'Guest';
      if (this.expertProfile && (this.expertProfile.first_name || this.expertProfile.last_name)) {
        name = `${this.expertProfile.first_name || ''} ${this.expertProfile.last_name || ''}`.trim();
      } else if (this.user && this.user.first_name && this.user.last_name) {
        name = `${this.user.first_name} ${this.user.last_name}`;
      } else if (this.user && this.user.first_name) {
        name = this.user.first_name;
      } else if (this.user && this.user.username) {
        name = this.user.username;
      }
      // Truncate if name is too long to fit
      const maxLength = 20;
      return name.length > maxLength ? name.slice(0, maxLength) + '...' : name;
    }
  },
  mounted() {
    this.fetchExpertProfile();
  },
  methods: {
    async fetchExpertProfile() {
      try {
        const response = await apiService.getExpertProfile();
        this.expertProfile = response.data;
      } catch (error) {
        console.error('Error fetching expert profile:', error);
      } finally {
        this.expertProfileLoaded = true;
      }
    },
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu;
    },
    logout() {
      // Use apiService to properly logout and handle token blacklisting
      apiService.logout()
        .then(() => {
          // Call the store action to clear auth state
          this.$store.commit('CLEAR_AUTH');
          
          // Redirect to login page
          this.$router.push('/login');
        })
        .catch(error => {
          console.error('Logout failed:', error);
          // Even if the API call fails, still clear local auth and redirect
          this.$store.commit('CLEAR_AUTH');
          this.$router.push('/login');
        });
    }
  }
}
</script>

<style scoped>
.sidebar {
  width: 220px;
  background-color: #20232a;
  color: white;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
}

.sidebar-logo {
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.sidebar-nav {
  flex: 1;
  padding: 20px 0;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: all 0.3s;
  border-left: 3px solid transparent;
}

.nav-item:hover,
.nav-item.active {
  background-color: rgba(255, 255, 255, 0.05);
  color: white;
  border-left: 3px solid #e94057;
}

.nav-item i, .nav-item .v-icon {
  margin-right: 12px;
}

.sidebar-footer {
  padding: 15px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
}

.user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 10px;
  background-color: #2e3b4e;
  flex-shrink: 0; /* Prevent avatar from shrinking */
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* Ensure image covers the entire circle without distortion */
  object-position: center; /* Center the image */
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background-color: #e94057;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px; /* Ensure consistent font size for initials */
}

.user-details {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0; /* Allow flex children to shrink below their content size */
  overflow: hidden; /* Ensure container handles overflow properly */
}

.user-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 5px;
  min-width: 0; /* Allow text to shrink and be truncated */
  font-size: 14px; /* Consistent font size */
  line-height: 1.2; /* Better line height */
}

.user-menu {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background-color: #2e3b4e;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 10;
  margin: 0 10px 5px;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s;
}

.menu-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.menu-item .v-icon {
  margin-right: 10px;
}
</style> 